<template>
  <template v-if="certificates !== null && !isLoading">
    <rbz-table
      :columns="columns"
      :table-data="certificates"
      :row-buttons="rowButtons"
      :row-classes="
        (item) => {
          return newCertificateId === item.id ? 'highlight-row' : '';
        }
      "
      :show-download-button="false"
      class="rbz-list-table"
      data-qa="server-certificates-table"
      :show-filter-row="true"
      :new-button-label="$t('certificate.serverToBackendMtlsCertificate.newCertificate')"
      new-button-classes="original-text"
      @new-button-clicked="openNewModal"
      @edit-row-button-clicked="openEditModal"
      @delete-row-button-clicked="openDeleteModal"
    >
      <template #tableResultsOverride="props">
        {{ $t('certificate.counter', props.numberOfResults) }}
      </template>
    </rbz-table>
    <div class="new-certificate-modal">
      <rbz-modal-dialog
        :show="showNewModal"
        :title="$t('certificate.serverToBackendMtlsCertificate.newModal.title')"
        :primary-btn-title="$t('common.operations.save.label')"
        @onPrimaryBtnClick="addNewCertificate"
        :secondary-btn-title="$t('common.operations.cancel.label')"
        @onSecondaryBtnClick="closeNewModal"
        @closeModal="closeNewModal"
        size="medium"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-name"
              variant="tertiary"
              class="certificate-name-input"
              data-qa="certificate-name-input"
              :label="$t('certificate.certificateName')"
              v-model:inputValue="newCertificateData.name"
              :placeholder="$t('certificate.certificateName')"
              :is-invalid="!!newCertificateNameError || !!newCertificateDuplicateError"
              :error-message-text="newCertificateNameError || newCertificateDuplicateError"
            >
              <template #postLabel>
                <div class="rbz-field-info">
                  <b-tooltip target="tooltip-certificate-name" triggers="hover click">
                    {{ $t('certificate.nameOptionalHelper') }}
                  </b-tooltip>
                  <icon-info-small
                    color="dusty-gray"
                    id="tooltip-certificate-name"
                    class="info-icon"
                  />
                </div>
              </template>
            </rbz-form-input>
          </div>
          <div class="rbz-field">
            <label class="rbz-label">
              {{ $t('certificate.certificateType') }}
            </label>
            <div class="certificate-type-wrapper">
              <rbz-form-radio
                id="certificate-type-manual"
                name="certificate-type"
                data-qa="certificate-type-manual-radio"
                :label="$t('certificate.radioManualInput')"
                @input="newCertificateData.isManualInput = 'manual'"
                :checked="newCertificateData.isManualInput === 'manual'"
              />
              <rbz-form-radio
                id="certificate-type-extraction"
                name="certificate-type"
                data-qa="certificate-type-extract-radio"
                :label="$t('certificate.radioExtractFile', { type: 'PFX' })"
                @input="newCertificateData.isManualInput = 'extract'"
                :checked="newCertificateData.isManualInput === 'extract'"
              />
            </div>
          </div>
          <layout-divider-line
            v-if="newCertificateData.isManualInput === 'manual'"
            color="fun-blue"
            divider-type="bold"
            class="certificate-details-divider"
          />
          <div v-if="newCertificateData.isManualInput === 'manual'">
            <span class="rbz-label">
              {{ $t('certificate.modalManualTitle') }}
            </span>
          </div>
          <div v-else>
            <span class="rbz-label">
              {{ $t('certificate.modalExtractionTitle') }}
            </span>
            <div class="rbz-field">
              <div class="file has-name">
                <label class="file-label">
                  <input
                    class="file-input"
                    type="file"
                    @input="loadFile($event)"
                    name="certFileInput"
                    data-qa="file-input"
                    accept=".pfx"
                  />
                  <span class="file-cta">
                    <span class="file-icon">
                      <icon-upload />
                    </span>
                    <span class="file-label">
                      {{ $t('certificate.chooseFile', { type: 'PFX' }) }}
                    </span>
                  </span>
                  <span class="file-name">
                    {{ newCertificateData.file?.name || $t('common.noFileChosen') }}
                  </span>
                </label>
              </div>
            </div>
            <div
              class="rbz-field"
              v-show="newCertificateData.file && !newCertificateData.privateKey"
            >
              <span class="rbz-label">
                {{ $t('common.password') }}
              </span>
              <div class="password-wrapper">
                <rbz-form-input
                  name="file-password"
                  class="file-password"
                  data-qa="file-password-input"
                  :placeholder="$t('common.password')"
                  ref="pfxPass"
                  v-model:inputValue="pfxPassword"
                  :is-invalid="isPasswordWarning"
                  :error-message-text="newCertificateData.passwordMessage"
                />
                <rbz-form-button
                  :label="$t('certificate.extractFileButton')"
                  :disabled="isExtractFileNil || !pfxPassword"
                  @button-click="extractFile()"
                  class="extract-pfx-button"
                  data-qa="extract-pfx-button"
                />
              </div>
            </div>
          </div>
          <layout-divider-line
            v-if="newCertificateData.isManualInput === 'extract'"
            color="fun-blue"
            divider-type="bold"
            class="certificate-details-divider"
          />
          <div class="rbz-field">
            <rbz-form-textarea
              name="private-key-textarea"
              :label="$t('certificate.privateKey')"
              v-model:inputValue="newCertificateData.privateKey"
              :placeholder="$t('certificate.privateKeyPlaceholder')"
              :disabled="newCertificateData.isManualInput !== 'manual'"
              rows="6"
              class="private-key-textarea"
              data-qa="private-key-textarea"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="certificate-body-textarea"
              :label="$t('certificate.certificateBody')"
              v-model:inputValue="newCertificateData.certificate"
              :placeholder="$t('certificate.certificateBodyPlaceholder')"
              rows="6"
              class="certificate-body-textarea"
              data-qa="certificate-body-textarea"
            />
          </div>
        </template>
      </rbz-modal-dialog>
    </div>
    <div class="edit-certificate-modal">
      <rbz-modal-dialog
        :show="showEditModal"
        :title="$t('certificate.serverToBackendMtlsCertificate.editModal.title')"
        :subtitle="selectedCertificate?.name"
        :primary-btn-title="$t('common.operations.save.label')"
        @onPrimaryBtnClick="editCertificate"
        :secondary-btn-title="$t('common.operations.cancel.label')"
        @onSecondaryBtnClick="closeEditModal"
        :tertiary-btn-title="
          isCertDownloadEnabled ? $t('certificate.downloadFile', { type: 'PFX' }) : ''
        "
        :tertiary-btn-is-loading="isDownloadLoading"
        @onTertiaryBtnClick="downloadPFX"
        @closeModal="closeEditModal"
        size="medium"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-name-edit"
              variant="tertiary"
              class="certificate-name-edit-input"
              data-qa="certificate-name-edit-input"
              :label="$t('certificate.certificateName')"
              v-model:inputValue="selectedCertificate.name"
              :placeholder="$t('certificate.certificateName')"
              :is-invalid="!!editCertificateNameError || !!editCertificateDuplicateError"
              :error-message-text="editCertificateNameError || editCertificateDuplicateError"
            >
              <template #postLabel>
                <div class="rbz-field-info">
                  <b-tooltip target="tooltip-certificate-name-edit" triggers="hover click">
                    {{ $t('certificate.nameOptionalHelper') }}
                  </b-tooltip>
                  <icon-info-small
                    color="dusty-gray"
                    id="tooltip-certificate-name-edit"
                    class="info-icon"
                  />
                </div>
              </template>
            </rbz-form-input>
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="connected-backend-services"
              class="connected-backend-services"
              data-qa="connected-backend-services-input"
              :label="$t('certificate.connectedBackendServices')"
              :inputValue="connectedBackendServices"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-subject"
              class="certificate-subject"
              data-qa="certificate-subject-input"
              :label="$t('certificate.certificateSubject')"
              :inputValue="selectedCertificate.subject"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-issuer"
              class="certificate-issuer"
              data-qa="certificate-issuer-input"
              :label="$t('certificate.issuer')"
              :inputValue="selectedCertificate.issuer"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="san"
              class="certificate-san"
              data-qa="san-input"
              :label="$t('certificate.san')"
              :inputValue="selectedCertificate.san.toString()"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="certificate-body-textarea"
              :label="$t('certificate.certificateBody')"
              :inputValue="selectedCertificate['cert_body']"
              class="certificate-body-textarea"
              data-qa="certificate-body-textarea"
              disabled
            />
          </div>
          <certificates-tabs
            :selected-items="selectedApps"
            :all-items="backendServices"
            side="serverToBackendMTLS"
            :assigned-certificates="assignedCertificates"
            :selected-certificate="selectedCertificate"
            @update-selected-apps="updateSelectedApps"
            @update-replace-certificate="updateReplaceCertificate"
            ref="certificatesTabsRef"
          >
          </certificates-tabs>
        </template>
      </rbz-modal-dialog>
    </div>
    <certificates-list-delete-modal
      :selected-certificate="selectedCertificate"
      side="serverToBackendMTLS"
      :attached-apps="attachedApps"
      :entity-name="entityName"
      @delete-certificate="deleteCertificate"
      ref="certificatesDeleteModalRef"
    >
    </certificates-list-delete-modal>
  </template>
  <rbz-loader :loading="!!isLoading" :data-exists="certificates !== null" height="100%" />
</template>
<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import {
  Balancer,
  GenericObject,
  Link,
  BackendService,
  ServerToBackendMtlsCertificate,
} from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import { useBranchesStore } from '@/store/BranchesStore';
import { mapStores } from 'pinia';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzLoader from '@/components/RbzLoader.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import CertificatesListDeleteModal from '@/components/CertificatesListDeleteModal.vue';
import CertificatesTabs from '@/components/CertificatesTabs.vue';
import IconUpload from 'ui-component-library/assets/icons/IconUpload.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';

export default defineComponent({
  name: 'CertificatesListServerToBackendMtlsTab',
  components: {
    IconInfoSmall,
    IconUpload,
    CertificatesTabs,
    CertificatesListDeleteModal,
    RbzLoader,
    LayoutDividerLine,
    RbzFormButton,
    RbzFormInput,
    RbzFormRadio,
    RbzFormTextarea,
    RbzModalDialog,
    RbzTable,
  },
  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Certificates') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadData();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
    assignedApps: {
      handler: function (val) {
        this.selectedApps = _.cloneDeep(val);
      },
      deep: true,
      immediate: true,
    },
  },
  data() {
    const defaultNewCertificateData = {
      isManualInput: 'manual',
      file: null,
      privateKey: '',
      certificate: '',
      password: '',
      passwordMessage: '',
      name: '',
    };
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('certificate.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'white-space-pre-line word-break-break-word',
          autoFocus: true,
        },
        {
          id: 'expiration-date',
          title: this.$t('certificate.expirationDate'),
          fieldNames: ['exp_date'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item: ServerToBackendMtlsCertificate) => {
            const sslDateUnix = Number(new Date(`${item['exp_date']}T00:00:00.000Z`));
            const currentDateUnix = Number(new Date());
            const ONE_MONTH = 1000 * 60 * 60 * 24 * 30;
            return sslDateUnix - currentDateUnix < ONE_MONTH ? 'has-text-danger' : '';
          },
        },
        {
          id: 'linked-to',
          title: this.$t('certificate.linkedTo'),
          displayFunction: (item: ServerToBackendMtlsCertificate) => {
            const matchingBackendServices = _.filter(
              this.backendServices,
              (backendService: BackendService) => {
                return backendService.mtls_certificate === item.id;
              }
            );
            return matchingBackendServices.length
              ? _.map(matchingBackendServices, 'name').join('\n')
              : '';
          },
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'san',
          title: this.$t('certificate.san'),
          fieldNames: ['san'],
          displayFunction: (item) => {
            return item.san?.join('\n') || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-280px',
          cellContentClasses: 'white-space-pre-line word-break-break-word',
        },
        {
          id: 'issuer',
          title: this.$t('certificate.issuer'),
          fieldNames: ['issuer'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'white-space-pre-line word-break-break-word',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      loadBalancers: null as Balancer[],
      certificates: null as ServerToBackendMtlsCertificate[],
      backendServices: null as BackendService[],
      pageLoadingCounter: null,
      showNewModal: false,
      showEditModal: false,
      defaultNewCertificateData: _.cloneDeep(defaultNewCertificateData),
      newCertificateData: _.cloneDeep(defaultNewCertificateData),
      isDownloadLoading: false,
      selectedCertificate: null as ServerToBackendMtlsCertificate,
      selectedApps: [] as BackendService[],
      attachedApps: [] as string[],
      replaceCertificate: null as ServerToBackendMtlsCertificate['id'],
      newCertificateId: '',
      isCertDownloadEnabled: false as boolean,
      newCertificateNameError: '',
      editCertificateNameError: '',
      newCertificateDuplicateError: '',
      editCertificateDuplicateError: '',
      certificatesInUse: [] as { name: string; id: string; side: string }[],
    };
  },
  computed: {
    Utils() {
      return Utils;
    },
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore),

    entityName() {
      return {
        plural: this.$t('certificate.serverToBackendMtlsCertificate.plural'),
        singular: this.$t('certificate.serverToBackendMtlsCertificate.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    backendServicesByCertNameMap(): GenericObject {
      const returnValue: { [key: string]: string[] } = {};
      this.backendServices.forEach((backendService: BackendService) => {
        const certId = backendService.mtls_certificate;
        if (certId) {
          if (!returnValue[certId]) {
            returnValue[certId] = [];
          }
          returnValue[certId].push(backendService.name);
        }
      });
      return returnValue;
    },

    connectedBackendServices(): string {
      const backendServices = this.backendServicesByCertNameMap[this.selectedCertificate.id];
      return backendServices?.join(', ') || '';
    },

    assignedApps(): BackendService[] {
      return (
        _.filter(this.backendServices, (backendService: BackendService) => {
          return backendService?.mtls_certificate === this.selectedCertificate?.id;
        }) || []
      );
    },

    assignedCertificates(): ServerToBackendMtlsCertificate[] {
      const balancerAssignedCertificates: ServerToBackendMtlsCertificate[] = [];
      const backendServicesAssignedCertificates: ServerToBackendMtlsCertificate[] = [];
      this.loadBalancers.forEach((balancer: Balancer) => {
        _.forEach(balancer.certificates, (link: string) => {
          const certificateId = this.findLocalCertificateNameWithLink(link);
          const certificate = _.find(this.certificates, (certificate) => {
            return certificate.id === certificateId;
          });
          if (certificate) {
            balancerAssignedCertificates.push(certificate);
          }
        });
        const defaultCertificateId = this.findLocalCertificateNameWithLink(
          balancer.default_certificate
        );
        const certificate = _.find(this.certificates, (certificate) => {
          return certificate.id === defaultCertificateId;
        });
        if (certificate) {
          balancerAssignedCertificates.push(certificate);
        }
      });
      _.forEach(this.backendServices, (backendService: BackendService) => {
        const certificate = _.find(this.certificates, (certificate) => {
          return certificate.id === backendService.mtls_certificate;
        });
        if (certificate) {
          backendServicesAssignedCertificates.push(certificate);
        }
      });
      return _.union(balancerAssignedCertificates, backendServicesAssignedCertificates);
    },

    pfxPassword: {
      get(): string {
        return '•'.repeat(this.newCertificateData.password.length);
      },
      set(value: string) {
        const newVal = value.replaceAll('•', '');
        const password = this.newCertificateData.password.slice(0, value.length);
        this.newCertificateData.passwordMessage = '';
        this.newCertificateData.password = `${password}${newVal}`;
      },
    },

    isPasswordWarning(): boolean {
      return !!this.newCertificateData.passwordMessage;
    },

    isExtractFileNil(): boolean {
      return _.isNil(this.newCertificateData.file);
    },
  },
  methods: {
    validateCertificateName(name: string): boolean {
      const nameRegex = /^[a-zA-Z0-9_-]*$/;
      if (name && !nameRegex.test(name)) {
        return false;
      }
      return true;
    },

    getCertificateTypeByName(name: string, excludeId?: string): string {
      if (!name || !this.certificatesInUse) return '';
      const certType = this.certificatesInUse
        .filter((cert) => cert.id !== excludeId)
        .find((cert) => cert.name?.toLowerCase() === name.toLowerCase())?.side;
      return certType || '';
    },

    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadData() {
      this.setPageLoadingStatus(true);
      const loadBalancersPromise = this.loadLoadBalancers();
      const certificatesPromise = this.loadCertificates();
      const backendServicesPromise = this.loadBackendServices();
      const isCertDownloadEnabledPromise = this.loadIsCertDownloadEnabled();
      await Promise.all([
        loadBalancersPromise,
        certificatesPromise,
        backendServicesPromise,
        isCertDownloadEnabledPromise,
      ]);
      this.setPageLoadingStatus(false);
    },

    async loadLoadBalancers() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/load-balancers`,
      });
      this.loadBalancers = response?.data?.items || [];
      this.loadBalancers = this.loadBalancers.map((balancer) => {
        return {
          ...balancer,
          id: `ui-lb-${DatasetsUtils.generateUUID2()}`,
        };
      });
      this.setPageLoadingStatus(false);
    },

    async loadCertificates() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/certificates`;
      const certificatesResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      this.certificates = certificatesResponse?.data?.items || [];
      this.certificatesInUse = this.certificates.map((certificate) => {
        return { name: certificate.name, id: certificate.id, side: certificate.side };
      });
      this.certificates = _.filter(
        this.certificates,
        (certificate: ServerToBackendMtlsCertificate) => {
          return certificate.side === 'serverToBackendMTLS';
        }
      );
      this.setPageLoadingStatus(false);
    },

    async loadBackendServices() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/backend-services`;
      const backendServicesResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      this.backendServices = backendServicesResponse?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadIsCertDownloadEnabled() {
      this.setPageLoadingStatus(true);
      const url = 'tools/versions';
      const response = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      if (response?.data) {
        this.isCertDownloadEnabled = response.data.cert_download_enabled;
      }
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(certificate: ServerToBackendMtlsCertificate) {
      return this.isCertificateReferenced(certificate);
    },

    isCertificateReferenced(certificate: ServerToBackendMtlsCertificate): boolean {
      return this.backendServices.some((backendService: BackendService) => {
        return backendService['mtls_certificate'] === certificate.id;
      });
    },

    deleteTooltip(certificate: ServerToBackendMtlsCertificate): string {
      if (this.isCertificateReferenced(certificate)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    // New certificate modal
    openNewModal() {
      this.showNewModal = true;
    },

    closeNewModal() {
      this.showNewModal = false;
      this.newCertificateData = _.cloneDeep(this.defaultNewCertificateData);
      this.newCertificateNameError = '';
      this.newCertificateDuplicateError = '';
    },

    loadFile(event: Event) {
      const target = event.target as HTMLInputElement;
      const file: File = target.files[0];
      const extension = file?.name?.split('.')?.pop();
      if (extension?.toLowerCase() === 'pfx') {
        this.newCertificateData.file = file;
        this.newCertificateData.privateKey = '';
        this.newCertificateData.certificate = '';
        this.$nextTick(() => this.$refs.pfxPass.focus());
      } else {
        target.value = null;
      }
    },

    async extractFile() {
      const url = 'tools/certificates/extractpfx';
      const formData = new FormData();
      formData.append('fileName', this.newCertificateData.file, this.newCertificateData.file.name);
      formData.append('password', this.newCertificateData.password);
      const failureMessage = this.$t('certificate.extractFailure', { type: 'PFX' });
      const response = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url,
        data: formData,
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
        failureMessage,
      });
      if (response?.data) {
        this.newCertificateData.privateKey = response.data['private_key'] || '';
        this.newCertificateData.certificate = response.data['certificate_body'] || '';
      }
    },

    newCertificate(): ServerToBackendMtlsCertificate {
      const factory = DatasetsUtils.newEntityFactory['server-to-backend-mtls-certificates'];
      return factory && factory();
    },

    async addNewCertificate() {
      const name = this.newCertificateData.name?.trim() || '';
      if (!this.validateCertificateName(name)) {
        this.newCertificateNameError = this.$t('certificate.nameValidationError');
        return;
      }
      this.newCertificateNameError = '';
      const certType = this.getCertificateTypeByName(name);
      if (certType !== '') {
        this.newCertificateDuplicateError = this.$t('certificate.nameDuplicateError', {
          name,
          type: certType,
        });
        return;
      }
      this.newCertificateDuplicateError = '';
      this.setPageLoadingStatus(true);
      const certificateToAdd = this.newCertificate();
      const trimmedName = (this.newCertificateData.name || '').trim();
      if (trimmedName !== '') {
        certificateToAdd.name = trimmedName;
      }
      certificateToAdd['private_key'] = this.newCertificateData.privateKey;
      certificateToAdd['cert_body'] = this.newCertificateData.certificate;
      const successMessage = this.$t('common.operations.new.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url: `conf/${this.selectedBranch}/certificates/${certificateToAdd.id}`,
        data: certificateToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.newCertificateId = certificateToAdd.id;
      }
      this.closeNewModal();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    // Edit certificate modal
    openEditModal(certificate: ServerToBackendMtlsCertificate) {
      this.selectedCertificate = _.cloneDeep(certificate);
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editCertificateNameError = '';
      this.editCertificateDuplicateError = '';
    },

    updateSelectedApps(newAppsIds: BackendService['id'][]) {
      this.selectedApps = _.filter(this.backendServices, (backendService) => {
        return newAppsIds.includes(backendService.id);
      });
    },

    updateReplaceCertificate(id: ServerToBackendMtlsCertificate['id'][]) {
      this.replaceCertificate = id;
    },

    findLocalCertificateNameWithLink(providerLink: string): string {
      const certificate = _.find(
        this.certificates,
        (certificate: ServerToBackendMtlsCertificate) => {
          const link: Link = _.find(certificate.provider_links, (link) => {
            return link.link === providerLink;
          });
          return !!link;
        }
      );
      if (certificate) {
        return certificate.id;
      }
      const defaultCertName = providerLink.split('/');
      return defaultCertName[defaultCertName.length - 1] + '(*)';
    },

    async editCertificate() {
      const name = this.selectedCertificate.name?.trim() || '';
      if (!this.validateCertificateName(name)) {
        this.editCertificateNameError = this.$t('certificate.nameValidationError');
        return;
      }
      this.editCertificateNameError = '';
      const certType = this.getCertificateTypeByName(name, this.selectedCertificate.id);
      if (certType !== '') {
        this.editCertificateDuplicateError = this.$t('certificate.nameDuplicateError', {
          name,
          type: certType,
        });
        return;
      }
      this.editCertificateDuplicateError = '';
      this.setPageLoadingStatus(true);
      const backendServicesToRemove = _.differenceBy(this.assignedApps, this.selectedApps, 'id');
      const backendServicesToAdd = _.differenceBy(this.selectedApps, this.assignedApps, 'id');
      const methodName = 'PUT';
      let urlArgs = '';
      for (const backendService of backendServicesToRemove) {
        const url = `conf/${this.selectedBranch}/backend-services/${backendService.id}`;
        backendService.mtls_certificate = '';
        await RequestsUtils.sendRequest({
          methodName: methodName,
          url,
          data: backendService,
        });
      }
      for (const backendService of backendServicesToAdd) {
        const url = `conf/${this.selectedBranch}/backend-services/${backendService.id}`;
        backendService['mtls_certificate'] = this.selectedCertificate.id;
        await RequestsUtils.sendRequest({
          methodName: methodName,
          url,
          data: backendService,
        });
      }
      const certificateRenew = this.selectedCertificate['le_auto_renew'];
      const certificateReplace = this.selectedCertificate['le_auto_replace'];
      const nameParam = encodeURIComponent(this.selectedCertificate.name || '');
      urlArgs = `?name=${nameParam}&le_auto_renew=${certificateRenew}&le_auto_replace=${certificateReplace}`;
      if (this.replaceCertificate) {
        urlArgs = `${urlArgs}&replace_cert_id=${this.replaceCertificate}`;
        this.replaceCertificate = null;
      }
      const url = `conf/${this.selectedBranch}/certificates/${this.selectedCertificate.id}${urlArgs}`;
      const successMessage = this.$t(
        'certificate.serverToBackendMtlsCertificate.editModal.attachSuccess'
      );
      const data = this.selectedCertificate;
      await RequestsUtils.sendRequest({
        methodName: methodName,
        url,
        data,
        successMessage,
      });
      this.closeEditModal();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    async downloadPFX() {
      this.isDownloadLoading = true;
      const response = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/certificates/${this.selectedCertificate.id}/pfx`,
        config: {
          responseType: 'blob',
        },
      });
      if (response) {
        Utils.downloadFile('certificate', 'pfx', response.data);
      } else {
        Utils.toast({
          message: this.$t('certificate.downloadFailure', { type: 'PFX' }),
          type: 'is-danger',
        });
      }
      this.isDownloadLoading = false;
    },

    // Delete certificate modal
    openDeleteModal(certificate: ServerToBackendMtlsCertificate) {
      this.selectedCertificate = _.cloneDeep(certificate);
      this.attachedApps = this.backendServicesByCertNameMap[certificate.id] || [];
      this.$refs.certificatesDeleteModalRef.openDeleteModal();
    },

    async deleteCertificate() {
      this.setPageLoadingStatus(true);
      await this.$refs.certificatesDeleteModalRef.deleteCertificate();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },
  },
});
</script>
<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

.certificate-type-wrapper {
  display: flex;
  gap: 16px;
}

:deep(.rbz-table .data-row) {
  &.highlight-row {
    background-color: map.get(colors.$colors, 'ice-blue');
  }
}

:deep(.rbz-table .multi-line) {
  -webkit-line-clamp: 10;
}

.attached-apps {
  margin-top: 16px;
}

:deep(.password-wrapper) {
  align-items: center;
  display: flex;
  gap: 16px;

  .input-holder {
    flex-grow: 1;
  }
}

.certificate-details-divider {
  margin: 20px 0;
}
</style>
